import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FTPAccount } from '../../types';
import { ftpAccountsApi } from '../../api/ftpAccounts';
import { serverInfoApi } from '../../api/serverInfo';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Modal } from '../UI/Modal';
import { QuotaLimitModal } from '../UI/QuotaLimitModal';
import { useQuotaGuard } from '../../hooks/useQuotaGuard';
import { CameraInstructions } from './CameraInstructions';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { track, EVENTS } from '../../lib/mixpanel';
import { clayStaggerContainer, clayStaggerItem, clayTapScale } from '../../constants/animations';
import styles from './FTPAccountsPanel.module.css';

interface FTPAccountsPanelProps {
  eventId: string;
  eventStatus: 'active' | 'ended';
  canManage?: boolean;
}

interface FTPInfo {
  host: string;
  port: number;
}

// ── Utility buttons ────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={styles['copy-btn']}
      title="Copy to clipboard"
    >
      {copied ? (
        <svg className={styles['copy-icon-success']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className={styles['copy-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
}

function PasswordCell({ password }: { password: string }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    track(EVENTS.FTP_PASSWORD_COPIED);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className={styles['password-cell']}>
      <span className={styles['password-value']}>
        {visible ? password : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
      </span>
      <motion.button
        onClick={() => setVisible(!visible)}
        className={styles['visibility-btn']}
        title={visible ? 'Hide' : 'Show'}
        whileHover={{ scale: 1.2 }}
        whileTap={clayTapScale}
      >
        {visible ? (
          <svg className={styles['visibility-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
          </svg>
        ) : (
          <svg className={styles['visibility-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )}
      </motion.button>
      <motion.button
        onClick={handleCopy}
        className={styles['copy-pw-btn']}
        title="Copy"
        whileHover={{ scale: 1.2 }}
        whileTap={clayTapScale}
      >
        {copied ? (
          <svg className={styles['copy-icon-success']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className={styles['copy-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </motion.button>
    </div>
  );
}

function CopyAllButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={styles['copy-all-btn']}
      title="Copy all connection details"
    >
      {copied ? (
        <>
          <svg className={styles['copy-all-icon-success']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className={styles['copy-all-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy all
        </>
      )}
    </button>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export const FTPAccountsPanel: React.FC<FTPAccountsPanelProps> = ({ eventId, eventStatus, canManage = true }) => {
  const [accounts, setAccounts] = useState<FTPAccount[]>([]);
  const [ftpInfo, setFtpInfo] = useState<FTPInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const { quotaModal, handleQuotaError, closeQuotaModal } = useQuotaGuard();
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ photographerName: '' });
  const [expandedInstructions, setExpandedInstructions] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [eventId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [serverInfo, accts] = await Promise.all([
        serverInfoApi.get(),
        ftpAccountsApi.list(eventId),
      ]);
      setFtpInfo(serverInfo.ftp);
      setAccounts(accts);
    } catch {
      setError('Failed to load FTP info');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const account = await ftpAccountsApi.create(eventId, form);
      setAccounts((prev) => [account, ...prev]);
      setForm({ photographerName: '' });
      setShowCreate(false);
      track(EVENTS.FTP_ACCOUNT_CREATED, { event_id: eventId });
      toast.success('Credentials created');
      // Silent re-fetch ensures the list reflects the canonical server state
      ftpAccountsApi.list(eventId).then(setAccounts).catch(() => {});
    } catch (err: unknown) {
      if (handleQuotaError(err)) return;
      const e = err as { response?: { data?: { error?: string } } };
      const msg = e.response?.data?.error || 'Failed to create account';
      setError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await ftpAccountsApi.delete(eventId, deleteTarget);
      setAccounts((prev) => prev.filter((a) => a._id !== deleteTarget));
      setDeleteTarget(null);
      track(EVENTS.FTP_ACCOUNT_DELETED, { event_id: eventId });
      toast.success('FTP account deleted');
    } catch {
      setError('Failed to delete account');
      toast.error('Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  function buildCopyText(account: FTPAccount): string {
    if (!ftpInfo) return '';
    return [
      `Host: ${ftpInfo.host}`,
      `Port: ${ftpInfo.port}`,
      `Secure Protocol: OFF`,
      `Passive Mode: OFF (try ON if connection fails)`,
      `Username: ${account.username}`,
      `Password: ${account.passwordPlain}`,
      `Directory: /`,
    ].join('\n');
  }

  return (
    <div className={styles['wrapper']}>

      {loading && (
        <motion.div
          className={styles['character-peek']}
          animate={{ y: [0, -6, 0], rotate: [-8, -4, -8] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
        >
          <MachhaCharacter state="Loading" part="face" size={68} />
        </motion.div>
      )}

      {!loading && accounts.length === 0 && (
        <motion.div
          className={styles['character-peek-empty']}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1, rotate: [12, 16, 12] }}
          transition={{ rotate: { repeat: Infinity, duration: 3, ease: 'easeInOut' }, default: { duration: 0.4 } }}
        >
          <MachhaCharacter state="Empty" part="face" size={68} />
        </motion.div>
      )}

      <div className={styles['panel']}>

        {/* Header */}
        <div className={styles['header']}>
          <div>
            <h2 className={styles['header-title']}>FTP Accounts</h2>
            <p className={styles['header-subtitle']}>Credentials for photographer uploads</p>
          </div>
          <div className={styles['header-actions']}>
            {eventStatus === 'active' && canManage && (
              <Button variant="constructive" size="sm" onClick={() => setShowCreate(true)}>
                <svg className={styles['add-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Account
              </Button>
            )}
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              className={styles['error-banner']}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <div className={styles['body']}>

          {loading ? (
            <motion.div
              className={styles['loading-center']}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            >
              <p className={styles['loading-text']}>Loading...</p>
            </motion.div>
          ) : (
            <>
              {/* Server details */}
              {ftpInfo && (
                <section>
                  <h3 className={styles['section-title']}>Server</h3>
                  <div className={styles['server-card']}>
                    <div className={styles['server-rows']}>
                      <div className={styles['info-row']}>
                        <span className={styles['info-label']}>Host</span>
                        <div className={styles['info-value-group']}>
                          <span className={styles['info-value']}>{ftpInfo.host}</span>
                          <CopyButton text={ftpInfo.host} />
                        </div>
                      </div>
                      <div className={styles['info-row']}>
                        <span className={styles['info-label']}>Port</span>
                        <div className={styles['info-value-group']}>
                          <span className={styles['info-value']}>{ftpInfo.port}</span>
                          <CopyButton text={String(ftpInfo.port)} />
                        </div>
                      </div>
                      <div className={styles['info-row']}>
                        <span className={styles['info-label']}>Secure Protocol</span>
                        <span className={styles['info-value-amber']}>OFF</span>
                      </div>
                      <div className={styles['info-row']}>
                        <span className={styles['info-label']}>Passive Mode</span>
                        <span className={styles['info-value-text']}>Try <span className={styles['info-value-bold']}>OFF</span> first, then ON</span>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Photographer credentials */}
              <section>
                <h3 className={styles['section-title']}>Photographer Credentials</h3>
                {accounts.length === 0 ? (
                  <motion.div
                    className={styles['empty-state']}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className={styles['empty-title']}>No FTP accounts yet</p>
                    <p className={styles['empty-subtitle']}>Add accounts for photographers to upload photos</p>
                  </motion.div>
                ) : (
                  <motion.div
                    className={styles['accounts-grid']}
                    variants={clayStaggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    <AnimatePresence>
                      {accounts.map((account) => (
                        <motion.div
                          key={account._id}
                          variants={clayStaggerItem}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className={styles['account-card']}
                        >
                          <div className={styles['card-header']}>
                            <span className={styles['account-name']}>{account.photographerName}</span>
                            {eventStatus === 'active' && canManage && (
                              <motion.button
                                onClick={() => setDeleteTarget(account._id)}
                                className={styles['delete-account-btn']}
                                whileHover={{ scale: 1.2 }}
                                whileTap={clayTapScale}
                              >
                                <svg className={styles['delete-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </motion.button>
                            )}
                          </div>

                          <div className={styles['creds-section']}>
                            <div className={styles['info-row']}>
                              <span className={styles['cred-label']}>Username</span>
                              <div className={styles['info-value-group']}>
                                <span className={styles['username-value']}>{account.username}</span>
                                <CopyButton text={account.username} />
                              </div>
                            </div>
                            <div className={styles['info-row']}>
                              <span className={styles['cred-label']}>Password</span>
                              <PasswordCell password={account.passwordPlain} />
                            </div>
                          </div>

                          {ftpInfo && (
                            <div className={styles['card-footer']}>
                              <CopyAllButton text={buildCopyText(account)} />
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </motion.div>
                )}
              </section>

              {/* Camera-specific instructions */}
              {ftpInfo && (
                <section>
                  <button
                    onClick={() => {
                      if (!expandedInstructions) track(EVENTS.CAMERA_SETUP_GUIDE_OPENED, { event_id: eventId });
                      setExpandedInstructions(!expandedInstructions);
                    }}
                    className={styles['collapse-btn']}
                  >
                    <svg
                      className={[styles['collapse-chevron'], expandedInstructions ? styles['collapse-chevron-open'] : ''].filter(Boolean).join(' ')}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <h3 className={styles['collapse-title']}>
                      Camera-Specific Instructions
                    </h3>
                  </button>
                  {expandedInstructions && (
                    <div className={styles['collapse-content']}>
                      <CameraInstructions host={ftpInfo.host} port={ftpInfo.port} />
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        {/* Create account modal */}
        <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create FTP Account">
          <form onSubmit={handleCreate} className={styles['modal-form']}>
            <Input label="Photographer Name" placeholder="John Doe" value={form.photographerName}
              onChange={(e) => setForm((f) => ({ ...f, photographerName: e.target.value }))} required autoFocus />
            <p className={styles['modal-hint']}>
              Username and password will be auto-generated with easy-to-type credentials.
            </p>
            {error && <p className={styles['modal-error']}>{error}</p>}
            <div className={styles['modal-actions']}>
              <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" loading={creating}>Create Account</Button>
            </div>
          </form>
        </Modal>

        {/* Delete account modal */}
        <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete FTP Account">
          <div className={styles['modal-body']}>
            <p className={styles['modal-text']}>
              Are you sure you want to delete this FTP account? This action cannot be undone.
            </p>
            <div className={styles['modal-actions']}>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        </Modal>
      </div>
      <QuotaLimitModal {...quotaModal} onClose={closeQuotaModal} />
    </div>
  );
};
